/**
 * Brașov Gateway — Transylvanian Editorial Design
 * Design Movement: Editorial de lux cu rădăcini central-europene
 * Colors: Off-white cald (#faf9f7), Garnet/Burgundy (primar), Auriu mat (accent)
 * Typography: Playfair Display (display/headings) + Source Sans 3 (body)
 * Layout: Asimetric, full-width hero, grid 2/3 + 1/3, secțiuni alternate
 */

import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { Menu, X, ChevronRight, MapPin, Building2, Users, BookOpen, Phone, Globe } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

// Hero: Brașov Hollywood-style sign on Tampa Mountain
const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/QaoyojluwTNwGOIc.webp";
const HERO_IMG_MOBILE = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/fKQZpGASpaXpadxV.webp";
const STREET_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/BoWmOkTZytdMEuKi.webp";
const REALESTATE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp";
// Generated hero images for blog cards
const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp";
const ARTICLE6_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";



const NAV_LINKS = [
  { label: "Why Brașov", href: "#why-brasov" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const SERVICES = [
  {
    number: "01",
    icon: Building2,
    title: "Real Estate",
    desc: "Apartments, houses, land and commercial spaces. We guide you from search to signed contract — without surprises.",
    href: "/services/real-estate",
  },
  {
    number: "02",
    icon: Users,
    title: "Company Setup",
    desc: "Open a Romanian SRL in 14 working days. We handle the paperwork, you handle the business.",
    href: "/services/company-setup",
  },
  {
    number: "03",
    icon: MapPin,
    title: "Relocation Support",
    desc: "Bank accounts, schools, healthcare, utilities. Everything you need to feel at home from day one.",
    href: null,
  },
  {
    number: "04",
    icon: BookOpen,
    title: "Business Opportunities",
    desc: "Brașov is growing fast. We show you where the real opportunities are — before everyone else finds out.",
    href: null,
  },
];

const ARTICLE8_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp";
const ARTICLE9_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp";

const BLOG_POSTS = [
  {
    tag: "Real Estate",
    title: "How to buy an apartment in Brașov as a foreigner: the 2026 guide",
    excerpt: "From getting your Romanian NIF at ANAF to navigating the new Nordis Law protections for off-plan purchases. Everything a foreign buyer needs to know about the legal process, required documents, and closing costs in Brașov.",
    img: ARTICLE9_IMG,
    href: "/buying-apartment-foreigner-brasov",
  },
  {
    tag: "Business",
    title: "Company setup in Romania in 2026: the complete guide for expats and foreign investors",
    excerpt: "Romania's 1% micro-enterprise tax is still the most competitive rate in the EU — but the rules have changed. Here is what it actually takes to open and run an SRL in 2026.",
    img: ARTICLE8_IMG,
    href: "/company-setup-romania",
  },
  {
    tag: "Relocation",
    title: "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood",
    excerpt: "Rents have roughly doubled in six years, property prices hit record highs in 2025. Here are the actual numbers — no tourist-brochure spin.",
    img: ARTICLE4_IMG,
    href: "/cost-of-living-brasov",
  },
  {
    tag: "City Guide",
    title: "Brașov, straight up: beyond the bear photos and the mountain air clichés",
    excerpt: "If you are thinking about moving your life and your money here, you have probably already heard the tired line about the city at the foot of Tampa Mountain. Skip the poetry.",
    img: ARTICLE1_IMG,
    href: "/why-expats-choose-brasov",
  },
  {
    tag: "City Guide",
    title: "What to do in Brașov: a guide for people who are tired of tourist guides",
    excerpt: "The old town is small enough to walk in an hour, which means most people do exactly that — and miss everything worth finding.",
    img: ARTICLE2_IMG,
    href: "/what-to-do-in-brasov-city-guide",
  },
  {
    tag: "Real Estate",
    title: "Brașov real estate: a decade of prices that kept going up — and why they're not done yet",
    excerpt: "In roughly ten years, prices in Brașov have nearly tripled. The question worth asking is whether that curve is still going up, or whether you've already missed the train.",
    img: ARTICLE3_IMG,
    href: "/buying-apartment-brasov",
  },
];

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const [contactForm, setContactForm] = useState({ name: "", email: "", interest: "realestate", message: "" });
  const [contactStatus, setContactStatus] = useState<"idle"|"loading"|"success"|"error">("idle");

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterStatus("loading");
    try {
      await subscribeToNewsletter(newsletterEmail);
      setNewsletterStatus("success");
    } catch { setNewsletterStatus("error"); }
  };

  const handleContactForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email) return;
    setContactStatus("loading");
    try {
      const res = await fetch("https://formspree.io/f/xreoorlo", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ ...contactForm, _subject: "Contact from homepage", source: "homepage-contact" }),
      });
      setContactStatus(res.ok ? "success" : "error");
    } catch { setContactStatus("error"); }
  };


  // Scroll to hash section when navigating from another page (e.g. /#services)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to let React render the page first
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
    <Helmet>
      <title>Brașov Gateway — Relocation & Real Estate Services for Foreigners</title>
      <meta name="description" content="Brașov Gateway helps foreigners relocate to Brașov, Romania. Real estate advisory, company setup, and relocation support — done right the first time." />
      <meta property="og:title" content="Brașov Gateway — Relocation & Real Estate Services for Foreigners" />
      <meta property="og:description" content="Brașov Gateway helps foreigners relocate to Brașov, Romania. Real estate advisory, company setup, and relocation support — done right the first time." />
      <meta property="og:image" content="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/QaoyojluwTNwGOIc.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Brașov Gateway — Relocation & Real Estate Services for Foreigners" />
      <link rel="canonical" href="https://brasovgateway.com/" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://brasovgateway.com/#organization",
              "name": "Brașov Gateway",
              "url": "https://brasovgateway.com",
              "logo": "https://brasovgateway.com/logo.png",
              "description": "Real estate advisory, company setup, and relocation support for foreigners moving to Brașov, Romania.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Brașov",
                "addressCountry": "RO"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "url": "https://brasovgateway.com/contact"
              },
              "sameAs": []
            },
            {
              "@type": "LocalBusiness",
              "@id": "https://brasovgateway.com/#localbusiness",
              "name": "Brașov Gateway",
              "url": "https://brasovgateway.com",
              "email": "hello@brasovgateway.com",
              "telephone": "+40753033130",
              "description": "Real estate advisory, company setup, relocation support, and business opportunities for foreigners moving to or investing in Brașov, Romania.",
              "areaServed": [
                { "@type": "City", "name": "Brașov" },
                { "@type": "Country", "name": "Romania" }
              ],
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Services",
                "itemListElement": [
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Real Estate Advisory", "description": "Property search, negotiation, legal and notary assistance, post-purchase support in Brașov." } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Company Setup", "description": "Romanian SRL incorporation, fiscal registration (CUI), bank account assistance, ongoing tax compliance." } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Relocation Support", "description": "End-to-end relocation assistance for foreigners moving to Brașov, Romania." } },
                  { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Business Opportunities", "description": "Local market intelligence and business opportunity identification in Brașov and Romania." } }
                ]
              },
              "availableLanguage": [
                { "@type": "Language", "name": "Romanian" },
                { "@type": "Language", "name": "English" },
                { "@type": "Language", "name": "Arabic" }
              ],
              "founder": {
                "@type": "Person",
                "name": "Vasile Onița"
              },
              "sameAs": [
                "https://brasovgateway.com"
              ]
            },
            {
              "@type": "WebSite",
              "@id": "https://brasovgateway.com/#website",
              "url": "https://brasovgateway.com",
              "name": "Brașov Gateway",
              "publisher": { "@id": "https://brasovgateway.com/#organization" }
            }
          ]
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "'Source Sans 3', sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>

      {/* ── NAVIGATION ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="container flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 no-underline">
            <img
              src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/eZnGevZfetqkqCbq.webp"
              alt="Brașov Gateway"
              width={40}
              height={40}
              className="h-10 w-auto object-contain"
            />
            <span className="font-semibold text-stone-800 tracking-wide text-sm uppercase hidden sm:inline" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "0.12em" }}>
              Brașov Gateway
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="text-sm text-stone-600 hover:text-stone-900 transition-colors no-underline font-light tracking-wide">
                {l.label}
              </a>
            ))}
          </div>

          {/* Language indicator + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Globe size={13} className="text-stone-400" />
              <span className="text-xs px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>EN</span>
            </div>
            <a
              href="/contact"
              className="text-xs px-4 py-2 text-white no-underline transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)", fontFamily: "'Source Sans 3', sans-serif", letterSpacing: "0.08em" }}
            >
              GET IN TOUCH
            </a>
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden text-stone-700" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="text-sm text-stone-700 no-underline" onClick={() => setMenuOpen(false)}>
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-1.5 pt-2 border-t border-stone-100">
              <Globe size={13} className="text-stone-400" />
              <span className="text-xs px-2 py-1 rounded text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>EN</span>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[600px] flex items-end pb-20 pt-16">
        {/* Background image */}
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet={HERO_IMG_MOBILE} width="750" height="1334" />
            <source media="(min-width: 768px)" srcSet={HERO_IMG} width="1920" height="1279" />
            <img src={HERO_IMG} alt="Brașov sign on Tampa Mountain — gateway to life in Brașov, Romania" className="w-full h-full object-cover hero-img" width="1920" height="1279" fetchPriority="high" />
          </picture>
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.92) 0%, oklch(0.12 0.01 60 / 0.55) 45%, oklch(0.12 0.01 60 / 0.15) 75%, transparent 100%)" }} />
        </div>

        {/* Hero content — left-aligned, bottom */}
        <div className="container relative z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs text-stone-300 uppercase tracking-widest font-light">Brașov, Romania</span>
            </div>
            <h1 className="text-white leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 6vw, 4.5rem)", fontWeight: 700 }}>
              Your Gateway to<br />
              <em style={{ color: "oklch(0.82 0.10 55)" }}>Life in Brașov</em>
            </h1>
            <p className="text-stone-300 text-lg font-light mb-8 leading-relaxed max-w-lg">
              Real estate, business setup, and relocation support — for those who want to do it right the first time.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="/services"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm text-white no-underline transition-opacity hover:opacity-90"
                style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                EXPLORE SERVICES <ChevronRight size={14} />
              </a>
              <a href="#why-brasov"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm text-white border border-white/30 no-underline hover:bg-white/10 transition-colors"
                style={{ letterSpacing: "0.06em" }}>
                WHY BRAȘOV
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 flex flex-col items-center gap-2 opacity-50">
          <span className="text-white text-xs tracking-widest" style={{ writingMode: "vertical-rl" }}>SCROLL</span>
          <div className="w-px h-10 bg-white/50" />
        </div>
      </section>

      {/* ── WHY BRAȘOV ── */}
      <section id="why-brasov" className="py-24 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text side */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
                <span className="text-xs uppercase tracking-widest text-stone-600">Why Here</span>
              </div>
              <h2 className="mb-6 text-stone-900" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.2 }}>
                Brașov isn't just<br />
                <em>beautiful.</em> It's smart.
              </h2>
              <p className="text-stone-600 text-lg font-light leading-relaxed mb-6">
                Surrounded by the Carpathians, with a medieval old town that took 500 years to build and a new international airport that took considerably less — Brașov sits at the intersection of history and opportunity.
              </p>
              <p className="text-stone-600 font-light leading-relaxed mb-8">
                Property prices still make sense. Quality of life is high. Crime is low. And the people who already moved here? They don't tend to leave.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-stone-100">
                {[
                  { value: "300K+", label: "Population" },
                  { value: "~6%", label: "Gross rental yield" },
                  { value: "#3", label: "Most livable city in Romania" },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.38 0.12 15)" }}>
                      {stat.value}
                    </div>
                    <div className="text-xs text-stone-500 font-light">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Image side */}
            <div className="relative">
              <img src={STREET_IMG} alt="Brașov old town street" className="w-full h-[320px] md:h-[480px] object-cover" loading="lazy" />
              <div className="absolute bottom-4 left-4 md:-bottom-6 md:-left-6 bg-white p-4 md:p-5 shadow-xl max-w-[160px] md:max-w-[200px]">
                <div className="text-3xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.38 0.12 15)" }}>
                  896 AD
                </div>
                <div className="text-xs text-stone-500 font-light leading-snug">Year Brașov was first documented. Some things age well.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
        <div className="container">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
                <span className="text-xs uppercase tracking-widest text-stone-600">What We Do</span>
              </div>
              <h2 className="text-stone-900" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700 }}>
                Services
              </h2>
            </div>
            <a href="#contact" className="hidden md:inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors no-underline">
              All services <ChevronRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200">
            {SERVICES.map((s) => (
              s.href ? (
                <Link key={s.number} href={s.href} className="no-underline">
                  <div className="bg-white p-8 group cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full">
                    <div className="text-6xl font-bold mb-6 select-none" style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.92 0.02 75)" }}>
                      {s.number}
                    </div>
                    <s.icon size={22} className="mb-4" style={{ color: "oklch(0.38 0.12 15)" }} />
                    <h3 className="text-lg font-semibold mb-3 text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {s.title}
                    </h3>
                    <p className="text-stone-500 text-sm font-light leading-relaxed">{s.desc}</p>
                    <div className="mt-6 flex items-center gap-2 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                      LEARN MORE <ChevronRight size={12} />
                    </div>
                  </div>
                </Link>
              ) : (
                <div key={s.number} className="bg-white p-8 group cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <div className="text-6xl font-bold mb-6 select-none" style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.92 0.02 75)" }}>
                    {s.number}
                  </div>
                  <s.icon size={22} className="mb-4" style={{ color: "oklch(0.38 0.12 15)" }} />
                  <h3 className="text-lg font-semibold mb-3 text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {s.title}
                  </h3>
                  <p className="text-stone-500 text-sm font-light leading-relaxed">{s.desc}</p>
                  <div className="mt-6 flex items-center gap-2 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    COMING SOON <ChevronRight size={12} />
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ── REAL ESTATE BANNER ── */}
      <section id="realestate" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={REALESTATE_IMG} alt="Luxury apartment Brașov" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0" style={{ backgroundColor: "oklch(0.12 0.01 60 / 0.75)" }} />
        </div>
        <div className="container relative z-10">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-white/50" />
              <span className="text-xs uppercase tracking-widest text-white/60">Real Estate</span>
            </div>
            <h2 className="text-white mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.2 }}>
              Find your place<br />
              <em style={{ color: "oklch(0.82 0.10 55)" }}>in Brașov</em>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mb-8 text-lg">
              Apartments, houses, land, commercial spaces. We work directly with developers and agencies to find you the right property — at the right price.
            </p>
            <Link href="/services/real-estate"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm text-white border border-white/40 no-underline hover:bg-white/10 transition-colors"
              style={{ letterSpacing: "0.06em" }}>
              EXPLORE REAL ESTATE <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── BLOG ── */}
      <section id="blog" className="py-24 bg-white">
        <div className="container">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
                <span className="text-xs uppercase tracking-widest text-stone-600">From the Blog</span>
              </div>
              <h2 className="text-stone-900" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700 }}>
                From the blog
              </h2>
            </div>
            <a href="/blog" className="hidden md:inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors no-underline">
              All articles <ChevronRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.slice(0, 3).map((post, i) => (
              <Link key={i} href={post.href}>
                <article className="group cursor-pointer">
                  <div className="overflow-hidden mb-5">
                    <img src={post.img} alt={post.title} className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>{post.tag}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {post.title}
                  </h3>
                  <p className="text-stone-500 text-sm font-light leading-relaxed line-clamp-3">{post.excerpt}</p>
                  <span className="mt-4 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    READ MORE <ChevronRight size={12} />
                  </span>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section className="py-20" style={{ backgroundColor: "oklch(0.97 0.008 75)" }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs uppercase tracking-widest text-stone-600">Newsletter</span>
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
            </div>
            <h2 className="text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700 }}>
              One email a week.<br />
              <em style={{ color: "oklch(0.38 0.12 15)" }}>What's actually happening in Brașov.</em>
            </h2>
            <p className="text-stone-500 font-light leading-relaxed mb-10 text-lg">
              No tourism fluff. No sponsored content disguised as news. Just honest, useful information for people who are serious about moving to or investing in Brașov — delivered every Friday morning.
            </p>
            <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="your@email.com"
                value={newsletterEmail}
                onChange={e => setNewsletterEmail(e.target.value)}
                required
                className="flex-1 border border-stone-300 bg-white px-4 py-3 text-sm text-stone-800 outline-none focus:border-stone-500 transition-colors placeholder:text-stone-400"
              />
              <button
                type="submit"
                disabled={newsletterStatus === "loading" || newsletterStatus === "success"}
                className="px-6 py-3 text-sm text-white font-semibold transition-opacity hover:opacity-90 whitespace-nowrap disabled:opacity-60"
                style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                {newsletterStatus === "loading" ? "..." : newsletterStatus === "success" ? "✓ SUBSCRIBED" : "SUBSCRIBE"}
              </button>
            </form>
            {newsletterStatus === "error" && (
              <p className="text-xs text-red-500 mt-2 text-center">Something went wrong. Please try again.</p>
            )}
            <div className="flex items-start gap-2 mt-4 max-w-md mx-auto">
              <input type="checkbox" id="gdpr" className="mt-0.5 accent-stone-700 cursor-pointer" />
              <label htmlFor="gdpr" className="text-xs text-stone-600 font-light leading-relaxed text-left cursor-pointer">
                I agree to receive weekly emails from Brașov Gateway. No spam, unsubscribe anytime. Read our{" "}
                <a href="/privacy-policy" className="underline text-stone-500 hover:text-stone-700">Privacy Policy</a>.
              </label>
            </div>
            <p className="text-xs text-stone-600 mt-6 font-light">
              Joined by <strong className="text-stone-600">340+ readers</strong> from 18 countries — and counting.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <FaqSection />

      {/* ── CONTACT / CTA ── */}
      <section id="contact" className="py-24" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
                <span className="text-xs uppercase tracking-widest text-stone-300">Get in Touch</span>
              </div>
              <h2 className="text-white mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, lineHeight: 1.2 }}>
                Ready to make<br />
                <em style={{ color: "oklch(0.82 0.10 55)" }}>Brașov your home?</em>
              </h2>
              <p className="text-stone-300 font-light leading-relaxed text-lg">
                Whether you're buying property, setting up a company, or just trying to figure out if Brașov is right for you — we're here to help. No sales pitch, just honest advice.
              </p>
            </div>

            {/* Contact form */}
            <form onSubmit={handleContactForm} className="bg-white/5 border border-white/10 p-5 md:p-8">
              {contactStatus === "success" ? (
                <div className="text-center py-12">
                  <p className="text-white text-lg mb-2" style={{ fontFamily: "Playfair Display, serif" }}>Message sent.</p>
                  <p className="text-stone-300 text-sm">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs text-stone-300 uppercase tracking-widest block mb-2">First Name *</label>
                      <input type="text" required value={contactForm.name} onChange={e => setContactForm(p => ({...p, name: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 outline-none focus:border-white/30 transition-colors placeholder:text-stone-600" placeholder="Your name" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-300 uppercase tracking-widest block mb-2">Email *</label>
                      <input type="email" required value={contactForm.email} onChange={e => setContactForm(p => ({...p, email: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 outline-none focus:border-white/30 transition-colors placeholder:text-stone-600" placeholder="your@email.com" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-interest" className="text-xs text-stone-300 uppercase tracking-widest block mb-2">I'm interested in</label>
                    <select id="contact-interest" value={contactForm.interest} onChange={e => setContactForm(p => ({...p, interest: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-stone-300 text-sm px-4 py-3 outline-none focus:border-white/30 transition-colors">
                      <option value="realestate">Real Estate</option>
                      <option value="company">Company Setup</option>
                      <option value="relocation">Relocation Support</option>
                      <option value="business">Business Opportunities</option>
                      <option value="other">Just exploring</option>
                    </select>
                  </div>
                  <div className="mb-6">
                    <label className="text-xs text-stone-300 uppercase tracking-widest block mb-2">Message</label>
                    <textarea rows={4} value={contactForm.message} onChange={e => setContactForm(p => ({...p, message: e.target.value}))} className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 outline-none focus:border-white/30 transition-colors placeholder:text-stone-600 resize-none" placeholder="Tell us about your plans..." />
                  </div>
                  {contactStatus === "error" && (
                    <p className="text-xs text-red-400 mb-4">Something went wrong. Please try again or email us directly.</p>
                  )}
                  <button
                    type="submit"
                    disabled={contactStatus === "loading"}
                    className="w-full py-3 text-sm text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    {contactStatus === "loading" ? "SENDING..." : "SEND MESSAGE"}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

       {/* ── FOOTER ── */}
      <SiteFooter />
    </div>
    </>
  );
}

const FAQ_ITEMS = [
  {
    q: "Is Brașov a good place to live for foreigners?",
    a: "Yes — Brașov consistently ranks among Romania’s top three most livable cities. It offers a compact, walkable historic centre, low crime, excellent air quality, fast internet, and a growing international community. English is widely spoken in professional and service environments. The city is large enough to have everything you need, small enough that you don’t feel lost."
  },
  {
    q: "How much does it cost to rent an apartment in Brașov?",
    a: "As of 2026, a furnished 1-bedroom apartment in the historic centre costs €450–750/month. Outside the centre (Tractorul, Coresi, Noua), expect €350–550/month. A studio starts from around €300/month. Prices have risen steadily since 2022 but remain significantly below Bucharest and Cluj-Napoca."
  },
  {
    q: "Can foreigners buy property in Brașov?",
    a: "EU citizens can buy property in Romania without restrictions, including land. Non-EU citizens can purchase apartments and buildings freely, but buying land requires a Romanian legal entity (such as an SRL company). The purchase process typically takes 4–8 weeks and requires a notary, a fiscal record check, and registration with the Land Registry (ANCPI)."
  },
  {
    q: "How do I open a company in Romania as a foreigner?",
    a: "The most common structure for foreigners is an SRL (Societate cu Răspundere Limitată) — the Romanian equivalent of a Limited Liability Company. Registration takes approximately 14 working days and requires a minimum share capital of 500 RON. A PFA (Persoană Fizică Autorizată) is a simpler sole-trader structure suited to freelancers and consultants. We handle the entire process, including fiscal registration and bank account opening."
  },
  {
    q: "Is Brașov safe?",
    a: "Brașov is one of the safest cities in Romania and compares favourably with most Western European cities. Violent crime is rare. The main concerns are petty theft in tourist areas during peak season and traffic in the city centre. Expat families consistently report feeling safe, including walking at night in the historic centre."
  },
  {
    q: "What is the cost of living in Brașov compared to Western Europe?",
    a: "Brașov is roughly 40–60% cheaper than major Western European cities for everyday expenses. Groceries, restaurants, utilities, and healthcare are significantly more affordable. Housing is the biggest variable — while cheaper than Paris, London, or Amsterdam, Brașov prices have risen sharply since 2020. A single professional can live comfortably on €1,200–1,800/month; a family of three on €2,300–3,300/month."
  },
  {
    q: "Does Brașov have an international airport?",
    a: "Yes — Brașov-Ghimbav International Airport opened in 2023 and is located approximately 15 km from the city centre. It operates scheduled flights to several European destinations. Bucharest Henri Coandă Airport (OTP) is approximately 2.5 hours by road and offers a much wider range of international connections."
  },
  {
    q: "What is the rental yield for property investment in Brașov?",
    a: "According to Global Property Guide (Q1 2026), the average gross rental yield in Brașov is approximately 6%, with studios and 3-bedroom apartments performing slightly above average. Net yields (after taxes, maintenance, and management fees) are typically 1.5–2% lower. Short-term rental yields via platforms like Airbnb can be higher in central locations and near Poiana Brașov ski resort."
  }
];

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-24 bg-white" style={{ borderTop: "1px solid oklch(0.88 0.005 75)" }}>
      {/* FAQPage Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.map(item => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a
              }
            }))
          })
        }}
      />
      <div className="container">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
            <span className="text-xs uppercase tracking-widest" style={{ color: "oklch(0.38 0.12 15)" }}>FAQ</span>
          </div>
          <h2 className="text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 700, lineHeight: 1.15 }}>
            Questions we hear every week
          </h2>
          <p className="text-stone-500 font-light mb-12" style={{ fontSize: "1.05rem" }}>
            Straight answers — no fluff, no sales pitch.
          </p>
          <div className="divide-y divide-stone-200">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="py-5">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-start justify-between gap-4 text-left group"
                  aria-expanded={openIndex === i}
                >
                  <span
                    className="text-stone-800 font-medium transition-colors group-hover:text-stone-900"
                    style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem", lineHeight: 1.4 }}
                  >
                    {item.q}
                  </span>
                  <span
                    className="flex-shrink-0 mt-1 transition-transform duration-300"
                    style={{
                      color: "oklch(0.38 0.12 15)",
                      transform: openIndex === i ? "rotate(45deg)" : "rotate(0deg)",
                      fontSize: "1.4rem",
                      lineHeight: 1
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: openIndex === i ? "500px" : "0px", opacity: openIndex === i ? 1 : 0 }}
                >
                  <p className="pt-3 pb-1 text-stone-600 font-light leading-relaxed" style={{ fontSize: "0.97rem", lineHeight: 1.75 }}>
                    {item.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
